// Вопрос агента к человеку (эскалация) с формой ответа.
import { useId, useState } from 'react'
import { ApiError, api, uploadFile } from '../api.ts'
import { useBusy } from '../lib/hooks.ts'
import { ESCALATION_TONES, SCOPE_LABELS, formatDateTime, timeAgo, truncate } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { TaskLink } from '../router.tsx'
import { useLive, useNow } from '../store.ts'
import { toast, toastError } from '../toast.ts'
import { ESCALATION_TYPE_LABELS, type AttachmentInfo, type Escalation } from '../types.ts'
import { FilePicker, MAX_FILE_BYTES, PendingFiles } from './Attachments.tsx'
import { ActorLabel, AgentLabel } from './AgentAvatar.tsx'
import { Icon } from './Icon.tsx'
import { Badge, Button, Collapsible, Kbd, cx } from './ui.tsx'

type Scope = 'global' | 'direction' | 'service'

/** Агент просит секрет: значение — в скрытое поле, в хранилище OMA; агенту — только имя для подстановки. */
function SecretForm({ esc, onAnswered }: { esc: Escalation; onAnswered?: (e: Escalation) => void }) {
  const [value, setValue] = useState('')
  const [shown, setShown] = useState(false)
  const [busy, run] = useBusy()
  const id = useId()
  const submit = () =>
    run(async () => {
      if (!value.trim()) {
        toast('Вставьте значение', 'error')
        return
      }
      try {
        const updated = await api.post<Escalation>(`/api/escalations/${esc.id}/answer`, { secret: value.trim() })
        setValue('')
        toast(`${esc.secret_name} передан — агент продолжит работу`, 'success')
        onAnswered?.({ ...esc, ...updated })
      } catch (e) {
        toastError(e, 'Не передано')
      }
    })
  return (
    <form
      className="mt-3 flex flex-col gap-2"
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <label htmlFor={id} className="lbl flex min-w-0 items-center gap-1.5">
        <Icon name="key" size={13} className="shrink-0" /> <span className="min-w-0 [overflow-wrap:anywhere]">{esc.secret_name}</span>
      </label>
      <div className="flex flex-wrap gap-2">
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          className="ctl min-w-[min(220px,100%)] flex-1 font-mono"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="new-password"
          spellCheck={false}
          placeholder="вставьте значение"
        />
        <Button type="button" variant="ghost" onClick={() => setShown((x) => !x)}>
          {shown ? 'Скрыть' : 'Показать'}
        </Button>
        <Button type="submit" variant="primary" loading={busy}>
          Передать
        </Button>
      </div>
      <p className="text-[12px] text-subtle">
        Хранится в OMA зашифрованным. Агент значение не увидит: OMA подставит его только в команду записи в настройки сервиса.
      </p>
    </form>
  )
}

/** Агент подготовил сообщение от вашего имени: поправить и отправить в задачу Битрикс24 кнопкой — или скопировать и отправить самому. */
function PostForm({ esc, onAnswered }: { esc: Escalation; onAnswered?: (e: Escalation) => void }) {
  const [text, setText] = useState(esc.post_text ?? '')
  const [other, setOther] = useState(false)
  const [reply, setReply] = useState('')
  const [busy, run] = useBusy()
  const id = useId()
  const b24 = esc.post_target === 'bitrix_task' && esc.post_bitrix_task ? esc.post_bitrix_task : null
  // связь с Битрикс24 оборвалась после отправки: сообщение могло уйти — повтор только после проверки
  const [unsure, setUnsure] = useState(Boolean(esc.post_attempt_at))
  const send = () =>
    run(async () => {
      const post = (confirm: boolean) => api.post<Escalation>(`/api/escalations/${esc.id}/post`, { message: text.trim(), ...(confirm ? { confirm: true } : {}) })
      try {
        let updated: Escalation
        try {
          updated = await post(false)
        } catch (e) {
          if (!(e instanceof ApiError && e.status === 428) || !window.confirm(e.message)) throw e
          updated = await post(true)
        }
        if (!updated?.posted_at) throw new Error('Сообщение не отправлено')
        toast(`Отправлено в задачу Битрикс24 #${b24} от вашего имени — агент продолжит`, 'success')
        onAnswered?.({ ...esc, ...updated })
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && /могло уйти/.test(e.message)) setUnsure(true)
        toastError(e, 'Не отправлено')
      }
    })
  const answer = (body: string, ok: string) =>
    run(async () => {
      try {
        const updated = await api.post<Escalation>(`/api/escalations/${esc.id}/answer`, { answer: body, post_reply: true })
        toast(ok, 'success')
        onAnswered?.({ ...esc, ...updated })
      } catch (e) {
        toastError(e, 'Ответ не отправлен')
      }
    })
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text.trim())
      toast('Текст скопирован', 'success')
    } catch {
      toast('Не удалось скопировать — выделите текст вручную', 'error')
    }
  }
  const edited = text.trim() !== (esc.post_text ?? '').trim()
  return (
    <div className="mt-3 flex flex-col gap-2">
      <label htmlFor={id} className="lbl flex min-w-0 items-center gap-1.5">
        <Icon name="message" size={13} className="shrink-0" /> {b24 ? `В задачу Битрикс24 #${b24} — от вашего имени` : 'Текст — отправьте от своего имени'}
      </label>
      <textarea id={id} className="ctl min-h-[120px] text-[14px] leading-relaxed" value={text} onChange={(e) => setText(e.target.value)} />
      {unsure && b24 ? (
        <p className="flex items-start gap-1.5 text-[12.5px] text-warn" role="status">
          <Icon name="alert" size={13} className="mt-0.5 shrink-0" />
          Прошлая попытка отправки оборвалась — сообщение могло уйти. Проверьте обсуждение задачи Битрикс24 #{b24}, прежде чем отправлять снова.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {b24 ? (
          <Button variant="primary" icon="send" loading={busy} disabled={!text.trim()} onClick={() => void send()}>
            Отправить в Битрикс24
          </Button>
        ) : null}
        <Button variant={b24 ? 'ghost' : 'secondary'} icon="copy" onClick={() => void copy()}>
          Скопировать
        </Button>
        <Button
          variant={b24 ? 'ghost' : 'primary'}
          loading={busy && !b24}
          disabled={!text.trim()}
          onClick={() => void answer(`Владелец отправил сам${edited ? ' (с правками)' : ''}:\n\n${text.trim()}`, 'Отмечено — агент продолжит')}
        >
          Отправил сам
        </Button>
        <button type="button" className="ml-auto text-[12.5px] text-muted hover:text-fg hover:underline" onClick={() => setOther((x) => !x)}>
          {other ? 'Скрыть' : 'Не отправлять…'}
        </button>
      </div>
      {other ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (reply.trim()) void answer(`Не отправлять. ${reply.trim()}`, 'Ответ отправлен — агент продолжит')
          }}
        >
          <textarea className="ctl min-h-[60px]" placeholder="Почему не отправлять и что делать вместо этого" value={reply} onChange={(e) => setReply(e.target.value)} />
          <div>
            <Button type="submit" size="sm" loading={busy} disabled={!reply.trim()}>
              Ответить агенту
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  )
}

export function EscalationCard({
  esc,
  onAnswered,
  showTask = true,
  compact = false,
}: {
  esc: Escalation
  onAnswered?: (e: Escalation) => void
  showTask?: boolean
  compact?: boolean
}) {
  const now = useNow()
  const directions = useLive((s) => s.directions)
  const services = useLive((s) => s.services)
  const [answer, setAnswer] = useState('')
  const [picked, setPicked] = useState<number | null>(null)
  const [save, setSave] = useState(false)
  const [decTitle, setDecTitle] = useState('')
  const [scope, setScope] = useState<Scope>('global')
  const [scopeId, setScopeId] = useState('')
  const [busy, run] = useBusy()
  const ids = { answer: useId(), save: useId(), title: useId(), scope: useId(), scopeId: useId() }
  // файлы и скриншоты к ответу: кнопкой, вставкой в поле ответа или перетаскиванием на карточку
  const [files, setFiles] = useState<File[]>([])
  const [progress, setProgress] = useState<(number | undefined)[]>([])
  const addFiles = (fs: File[]) => {
    const big = fs.filter((f) => f.size > MAX_FILE_BYTES)
    if (big.length) toast(`Больше 100 МБ, не прикреплено: ${big.map((f) => f.name).join(', ')}`, 'error')
    const ok = fs.filter((f) => f.size <= MAX_FILE_BYTES && f.size > 0)
    if (ok.length) setFiles((cur) => [...cur, ...ok])
  }

  const submit = () =>
    run(async () => {
      const text = answer.trim()
      if (!text && !files.length) {
        toast('Напиши ответ, выбери вариант или приложи файл', 'error')
        return
      }
      if (save && scope !== 'global' && !scopeId) {
        toast(scope === 'direction' ? 'Выбери направление для решения' : 'Выбери сервис для решения', 'error')
        return
      }
      try {
        const fileIds: number[] = []
        for (let i = 0; i < files.length; i++) {
          setProgress((p) => Object.assign([...p], { [i]: 0 }))
          const a = await uploadFile<AttachmentInfo>(files[i]!, (x) => setProgress((p) => Object.assign([...p], { [i]: x })))
          fileIds.push(a.id)
          setProgress((p) => Object.assign([...p], { [i]: 1 }))
        }
        const body: Record<string, unknown> = { answer: text }
        if (fileIds.length) body.attachments = fileIds
        if (save) {
          body.save_as_decision = true
          body.decision_title = decTitle.trim() || truncate(esc.question, 120)
          body.decision_scope_type = scope
          if (scope !== 'global') body.decision_scope_id = scopeId
        }
        const updated = await api.post<Escalation>(`/api/escalations/${esc.id}/answer`, body)
        toast(save ? 'Ответ отправлен и сохранён как решение' : 'Ответ отправлен — агент продолжит работу', 'success')
        setFiles([])
        setProgress([])
        onAnswered?.({ ...esc, ...updated })
      } catch (e) {
        setProgress([])
        toastError(e, 'Ответ не отправлен')
      }
    })

  const cancel = () =>
    run(async () => {
      if (!window.confirm('Снять вопрос без ответа? Агент узнает, что ответа не будет, и продолжит по своему усмотрению.')) return
      try {
        const updated = await api.post<Escalation>(`/api/escalations/${esc.id}/cancel`)
        toast('Вопрос снят', 'success')
        onAnswered?.({ ...esc, ...updated, status: updated?.status ?? 'cancelled' })
      } catch (e) {
        toastError(e, 'Не удалось снять вопрос')
      }
    })

  const answered = esc.status !== 'open'

  return (
    <article
      className={cx(
        'relative rounded-[12px] border border-line bg-surface shadow-card',
        compact ? 'px-4 py-3' : 'px-[18px] py-4',
      )}
    >
      {!answered ? <span aria-hidden="true" className="absolute inset-y-4 left-[-1px] w-[3px] rounded-r bg-warn" /> : null}
      <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] text-muted">
        <Badge tone={ESCALATION_TONES[esc.type]}>{ESCALATION_TYPE_LABELS[esc.type] ?? esc.type}</Badge>
        <AgentLabel id={esc.agent_id} size="sm" link className="font-medium text-fg" />
        {showTask && esc.task_id ? (
          // вопрос подзадачи — под корневой задачей владельца: решения принимаются в ней
          <TaskLink id={esc.root_id ?? esc.task_id} className="inline-flex min-w-0 max-w-full items-center gap-1 hover:text-fg hover:underline">
            <span className="shrink-0 font-mono text-[11.5px]">{esc.root_key || esc.task_key || `OMA-${esc.task_id}`}</span>
            {esc.root_title || esc.task_title ? <span className="min-w-0 max-w-[340px] truncate">{esc.root_title || esc.task_title}</span> : null}
            {esc.root_id && esc.root_id !== esc.task_id ? <span className="shrink-0 text-[11.5px] text-subtle">· подзадача {esc.task_key}</span> : null}
          </TaskLink>
        ) : null}
        <time className="tnum ml-auto shrink-0 text-[11.5px] text-subtle" title={formatDateTime(esc.created_at)}>
          {timeAgo(esc.created_at, now)}
        </time>
      </header>

      <div
        role="heading"
        aria-level={3}
        className={cx('mt-2.5 leading-snug font-semibold tracking-[-0.01em] text-fg', compact ? 'text-[14.5px]' : 'text-[17px]')}
      >
        <Markdown text={esc.question} />
      </div>

      {esc.context ? (
        <Collapsible summary={esc.type === 'action' ? 'Что написал агент' : 'Контекст'} className="mt-2" defaultOpen={esc.type === 'action' || (esc.type === 'approval' && esc.context.includes('```bash')) || (!compact && esc.context.length < 400)}>
          <div className="rounded-ctl border border-line bg-surface-2 px-3 py-2">
            <Markdown text={esc.context} className="text-[13px] text-fg-2" />
          </div>
        </Collapsible>
      ) : null}

      {esc.recommendation ? (
        <div className="mt-3 min-w-0 rounded-card border border-line bg-info-soft px-3 py-2.5">
          <div className="mb-1 flex items-center gap-1.5">
            <span className="lbl">Рекомендация агента</span>
            {!answered ? (
              <button
                type="button"
                className="ml-auto rounded-ctl px-2 py-0.5 text-[12px] font-semibold text-accent hover:bg-surface"
                onClick={() => {
                  setAnswer(esc.recommendation)
                  setPicked(null)
                }}
              >
                Принять
              </button>
            ) : null}
          </div>
          <Markdown text={esc.recommendation} className="text-[13.5px]" />
        </div>
      ) : null}

      {answered ? (
        <div className={cx('mt-3 rounded-card px-3 py-2.5', esc.status === 'cancelled' ? 'border border-line bg-surface-2' : 'bg-ok-soft')}>
          <div className={cx('mb-0.5 flex flex-wrap items-center gap-1.5 text-[12px]', esc.status === 'cancelled' ? 'text-muted' : 'text-ok')}>
            <Icon name={esc.status === 'cancelled' ? 'x' : 'check'} size={13} />
            <span className="font-semibold">{esc.status === 'cancelled' ? 'Вопрос снят' : 'Ответ'}</span>
            {esc.answered_by ? (
              <span className="text-muted">
                · <ActorLabel type="human" id={esc.answered_by} size="xs" />
              </span>
            ) : null}
            {esc.answered_at ? <span className="text-muted">· {timeAgo(esc.answered_at, now)}</span> : null}
          </div>
          {esc.answer ? <Markdown text={esc.answer} className="text-[13.5px] text-fg" /> : null}
        </div>
      ) : esc.secret_name ? (
        <SecretForm esc={esc} onAnswered={onAnswered} />
      ) : esc.post_text ? (
        <PostForm esc={esc} onAnswered={onAnswered} />
      ) : (
        <form
          className="mt-3 flex flex-col gap-2.5"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
          onDragOver={(e) => {
            if (Array.from(e.dataTransfer.types).includes('Files')) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          onDrop={(e) => {
            const fs = Array.from(e.dataTransfer.files ?? [])
            if (!fs.length) return
            e.preventDefault()
            e.stopPropagation()
            addFiles(fs)
          }}
          onPaste={(e) => {
            const fs = Array.from(e.clipboardData?.files ?? [])
            if (!fs.length) return
            if (!e.clipboardData.getData('text/plain')) e.preventDefault()
            e.stopPropagation()
            addFiles(fs)
          }}
        >
          {esc.options.length ? (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Варианты ответа">
              {esc.options.map((o, i) => (
                <button
                  key={i}
                  type="button"
                  aria-pressed={picked === i}
                  onClick={() => {
                    setPicked(i)
                    setAnswer(o)
                  }}
                  className={cx(
                    'max-w-full rounded-ctl border px-3 py-1.5 text-left text-[13px] [overflow-wrap:anywhere] transition-colors',
                    picked === i
                      ? 'border-accent/60 bg-accent-soft text-fg'
                      : 'border-line-strong bg-surface text-fg-2 hover:bg-surface-2',
                  )}
                >
                  {o}
                </button>
              ))}
            </div>
          ) : null}
          <label htmlFor={ids.answer} className="sr-only">
            Ответ агенту
          </label>
          <textarea
            id={ids.answer}
            className="ctl min-h-[70px]"
            placeholder="Твой ответ агенту: решение, уточнение, ограничения…"
            value={answer}
            onChange={(e) => {
              setAnswer(e.target.value)
              if (picked !== null && e.target.value !== esc.options[picked]) setPicked(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void submit()
              }
            }}
          />
          <div className="flex flex-col gap-2 rounded-ctl border border-line bg-surface-2 px-3 py-2">
            <label htmlFor={ids.save} className="flex cursor-pointer flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]">
              <input
                id={ids.save}
                type="checkbox"
                className="h-4 w-4 shrink-0"
                checked={save}
                onChange={(e) => setSave(e.target.checked)}
              />
              <span className="font-medium">Сохранить как решение</span>
              <span className="text-muted">— увидят все агенты</span>
            </label>
            {save ? (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_150px_180px]">
                <div>
                  <label htmlFor={ids.title} className="sr-only">
                    Название решения
                  </label>
                  <input
                    id={ids.title}
                    className="ctl"
                    placeholder={`Название: ${truncate(esc.question, 60)}`}
                    value={decTitle}
                    onChange={(e) => setDecTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor={ids.scope} className="sr-only">
                    Область действия
                  </label>
                  <select
                    id={ids.scope}
                    className="ctl"
                    value={scope}
                    onChange={(e) => {
                      setScope(e.target.value as Scope)
                      setScopeId('')
                    }}
                  >
                    <option value="global">{SCOPE_LABELS.global}</option>
                    <option value="direction">{SCOPE_LABELS.direction}</option>
                    <option value="service">{SCOPE_LABELS.service}</option>
                  </select>
                </div>
                {scope !== 'global' ? (
                  <div>
                    <label htmlFor={ids.scopeId} className="sr-only">
                      {scope === 'direction' ? 'Направление' : 'Сервис'}
                    </label>
                    <select id={ids.scopeId} className="ctl" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                      <option value="">{scope === 'direction' ? 'Направление…' : 'Сервис…'}</option>
                      {(scope === 'direction' ? directions : services).map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <PendingFiles files={files} progress={progress} onRemove={(i) => setFiles((cur) => cur.filter((_, k) => k !== i))} />
          <div className="flex flex-wrap items-center gap-2">
            <FilePicker onFiles={addFiles} size="xs" label="Файлы и скриншоты" />
            <Button variant="quiet" size="sm" onClick={() => void cancel()} disabled={busy} title="Закрыть вопрос без ответа">
              Снять вопрос
            </Button>
            <span className="ml-auto hidden text-[12px] text-subtle sm:inline">
              <Kbd>⌘</Kbd> <Kbd>Enter</Kbd> — отправить
            </span>
            <Button type="submit" variant="primary" icon="send" loading={busy} disabled={!answer.trim() && !files.length}>
              Ответить
            </Button>
          </div>
        </form>
      )}
    </article>
  )
}
